import React, { useEffect, useMemo, useRef, useState } from "react";
import * as yup from "yup";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import styled from "styled-components";
import html2canvas from "html2canvas";

import UserDataComponent from "./components/user.data.component";
import DeviceComponent from "./components/device.component";
import TouchComponent from "./components/touch.component";
import PaymentComponent from "./components/payment.component";
import StatusPaymentComponent from "./components/status-payment.component";
import DeviceKindSelectComponent from "./appliances/device-kind-select.component";
import ApplianceDeviceComponent from "./appliances/appliance-device.component";
import {
  APPLIANCE_MANUFACTURER,
  DeviceKind,
  MACCENTER_SPONSOR_ID,
} from "./appliances/constants";
import PrimaryButton from "../../../../components/buttons/PrimaryButton";
import { IUseCreatePolicyAgents } from "../../../../services/microservice/micelu/create/use-create-micelu.interface";
import {
  useCreatePolicyAgents,
  usePreCreatePolicyPaymentAgents,
} from "../../../../services/microservice/micelu/create/use-create-micelu";
import {
  useCreatePolicyAppliance,
  usePreCreatePolicyPaymentAppliance,
} from "../../../../services/microservice/appliances/use-appliances";
import { ICreatePolicyApplianceReq } from "../../../../services/microservice/appliances/appliances-service.interface";
import { useModalContext } from "../../../../providers/modal/modal.provider";
import ModalHeader from "../../../../components/claims/components/ModalHeader";
import ModalBody from "../../../../components/claims/components/ModalBody";
import { useCreateSimplyKioskoContext } from "./provider/createSimplyKiosko.provider";
import { useCreateKioskoContext } from "../provider/createKiosko.provider";
import { useKioskoConfig } from "../../context/kiosko-config.context";

interface IInputs {
  name: string;
  typeDocument: string;
  numberDocument: string;
  email: string;
  imei: string;
  insuredValue: string;
  plan: number;
  manufacturer: {
    id_system: string;
    nombre: string;
  };
  model: {
    device_name: string;
    reference_tac: string;
  };
  valuePolicie: string;
  confirm: boolean;
  category: {
    idSystem: string;
    name: string;
  };
  comercialName: string;
  applianceModel: string;
  serial: string;
}

const messageRequired = " Requerido";

const splitFullName = (
  fullName: string,
): { firstName: string; lastName: string } => {
  const words = fullName.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return { firstName: "", lastName: "" };
  if (words.length === 1) return { firstName: words[0], lastName: "" };
  if (words.length === 2) return { firstName: words[0], lastName: words[1] };
  if (words.length === 3)
    return { firstName: words[0], lastName: `${words[1]} ${words[2]}` };
  return {
    firstName: `${words[0]} ${words[1]}`,
    lastName: words.slice(2).join(" ") || "",
  };
};

const buildClientPolicy = (val: IInputs) => {
  const { firstName, lastName } = splitFullName(val.name);
  return {
    firstName,
    lastName,
    genderId: "POR_ACTUALIZAR",
    identification: {
      type: val.typeDocument,
      number: val.numberDocument,
    },
    email: val.email,
    address: "NA",
  };
};

const LabelCheckbox = styled.label`
  > input[type="checkbox"]:checked + i {
    background: "#000000" !important;
  }
`;

const buildSchema = (isUsed: boolean) =>
  yup.object().shape({
    name: yup.string().required(messageRequired),
    typeDocument: yup.string().required(messageRequired),
    numberDocument: yup
      .string()
      .matches(/^\d+$/, { message: messageRequired })
      .required(messageRequired),
    email: yup.string().email().required(messageRequired),
    imei: yup
      .string()
      .min(15, { message: messageRequired })
      .max(15, { message: messageRequired })
      .matches(/^\d+$/, { message: messageRequired })
      .required(messageRequired),
    insuredValue: isUsed
      ? yup.string().optional()
      : yup
          .string()
          .matches(/^\$\d+(?:,\d+)*$/, { message: messageRequired })
          .test("min-value", "Mínimo $2,000", (value) => {
            if (!value) return false;
            const numericValue = Number.parseInt(
              value.replaceAll("$", "").replaceAll(",", ""),
              10,
            );
            return numericValue > 2000;
          })
          .required(messageRequired),
    manufacturer: yup
      .object({
        id_system: yup.string().required(" Requerido"),
        nombre: yup.string().required(" Requerido"),
      })
      .required(" Requerido"),
    model: yup
      .object({
        device_name: yup.string().required(" Requerido"),
        reference_tac: yup.string().required(" Requerido"),
      })
      .required(" Requerido")
      .typeError(" Requerido"),
    plan: yup.number().required(messageRequired).typeError(messageRequired),
    confirm: yup.boolean(),
  });

const buildApplianceSchema = () =>
  yup.object().shape({
    name: yup.string().required(messageRequired),
    typeDocument: yup.string().required(messageRequired),
    numberDocument: yup
      .string()
      .matches(/^\d+$/, { message: messageRequired })
      .required(messageRequired),
    email: yup.string().email().required(messageRequired),
    category: yup
      .object({
        idSystem: yup.string().required(messageRequired),
        name: yup.string().required(messageRequired),
      })
      .required(messageRequired)
      .typeError(messageRequired),
    comercialName: yup.string().required(messageRequired),
    applianceModel: yup.string().required(messageRequired),
    serial: yup.string().required(messageRequired),
    insuredValue: yup
      .string()
      .matches(/^\$\d+(?:,\d+)*$/, { message: messageRequired })
      .test("min-value", "Mínimo $2,000", (value) => {
        if (!value) return false;
        const numericValue = Number.parseInt(
          value.replaceAll("$", "").replaceAll(",", ""),
          10,
        );
        return numericValue > 2000;
      })
      .required(messageRequired),
    plan: yup.number().required(messageRequired).typeError(messageRequired),
    confirm: yup.boolean(),
  });

const CreateSimplyComponent = () => {
  const config = useKioskoConfig();
  const { flow, setStep: setGeneralStep } = useCreateKioskoContext();
  const isUsed = flow === "used";
  const { setShow, modalRef } = useModalContext();
  const { step, setStep, setInfoPayment, setFormData, setResetFormCallback } =
    useCreateSimplyKioskoContext();
  const { mutateAsync: createPolicy } = useCreatePolicyAgents();
  const { mutateAsync: preCreatePolicyPayment } =
    usePreCreatePolicyPaymentAgents();
  const { mutateAsync: createPolicyAppliance } = useCreatePolicyAppliance();
  const { mutateAsync: preCreatePolicyPaymentAppliance } =
    usePreCreatePolicyPaymentAppliance();
  const [selectedPlan, setSelectedPlan] = useState<number>(null);
  const [planData, setPlanData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const submittingRef = useRef(false);
  const [backendInsuredValue, setBackendInsuredValue] = useState<string>("");
  const [pendingData, setPendingData] = useState<IUseCreatePolicyAgents | null>(
    null,
  );
  const [pendingCollectedInternally, setPendingCollectedInternally] =
    useState<boolean>(false);

  const isMaccenter = config.sponsorID === MACCENTER_SPONSOR_ID;
  const [deviceKind, setDeviceKind] = useState<DeviceKind | null>(
    isMaccenter ? null : "phone",
  );
  const isAppliance = deviceKind === "appliance";

  const goesThroughTouch = !isAppliance && isUsed;

  const schema = useMemo<any>(
    () => (isAppliance ? buildApplianceSchema() : buildSchema(isUsed)),
    [isUsed, isAppliance],
  );

  const {
    control,
    formState: { errors, isSubmitting },
    handleSubmit,
    watch,
    setValue,
    clearErrors,
    getValues,
    reset,
    register,
  } = useForm<IInputs>({
    // El schema cambia de forma según el flujo (celular vs appliance), así
    // que se castea: yupResolver no puede tipar un resolver "union" contra un
    // único IInputs que es superset de ambos.
    resolver: yupResolver(schema) as any,
    defaultValues: {
      name: "",
      typeDocument: "CEDULA_CIUDADANIA",
      numberDocument: "",
      email: "",
      imei: "",
      insuredValue: "",
      plan: undefined,
      confirm: false,
      comercialName: "",
      applianceModel: "",
      serial: "",
    } as any,
  });

  useEffect(() => {
    setResetFormCallback(() => reset);
  }, [reset, setResetFormCallback]);

  const buildPhonePolicyData = (val: IInputs): IUseCreatePolicyAgents => {
    const data: IUseCreatePolicyAgents = {
      client: buildClientPolicy(val),
      planId: selectedPlan,
      device: {
        line: "0000000000",
        imei: val.imei,
        referenceTac: val.model.reference_tac,
      },
      sponsorId: config.sponsorID,
      priceOptionId: Number(val.plan),
    };
    if (!isUsed) {
      data.insuredValue = (val.insuredValue || "")
        .replaceAll("$", "")
        .replaceAll(",", "");
    }
    return data;
  };

  const buildAppliancePolicyData = (
    val: IInputs,
  ): ICreatePolicyApplianceReq => ({
    client: buildClientPolicy(val),
    planId: selectedPlan,
    device: {
      commercialName: val.comercialName,
      model: val.applianceModel,
      manufacturer: APPLIANCE_MANUFACTURER,
      serial: val.serial,
      category: val.category?.idSystem,
    },
    sponsorId: config.sponsorID,
    priceOptionId: Number(val.plan),
    insuredValue: (val.insuredValue || "")
      .replaceAll("$", "")
      .replaceAll(",", ""),
  });

  const showErrorModal = (error: any) => {
    const message =
      error?.response?.status === 400
        ? error?.response?.data?.error?.message
        : "Acaba de ocurrir un problema, lo sentimos.";
    setShow((prevState) => ({
      ...prevState,
      visible: true,
      header: <ModalHeader success={false} />,
      content: (
        <ModalBody title=" No fue posible crear la póliza" message={message} />
      ),
      onAccept: clearProcess,
      titleOnAccept: "Aceptar",
      withClass: "flex",
      showBtnAccept: true,
    }));
  };

  const finalize = async (
    data: IUseCreatePolicyAgents,
    collectedInternally: boolean,
  ) => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    setIsLoading(true);
    try {
      if (collectedInternally) {
        const res = await preCreatePolicyPayment(data);
        if (res) {
          setInfoPayment({
            pregeneratePolicyId: res.data.pregeneratePolicyId,
          });
          setStep(3);
        }
      } else {
        const res = await createPolicy(data);
        if (res) {
          setShow((prevState) => ({
            ...prevState,
            visible: true,
            header: <ModalHeader success={true} />,
            content: (
              <ModalBody
                title="Creación póliza exitosa"
                message={`La póliza se creó exitosamente con id: ${res.data.policy.id}`}
              />
            ),
            onAccept: clearProcess,
            titleOnAccept: "Aceptar",
            withClass: "flex",
            showBtnAccept: true,
          }));
        }
      }
    } catch (error) {
      showErrorModal(error);
    } finally {
      submittingRef.current = false;
      setIsLoading(false);
    }
  };

  const finalizeAppliance = async (
    data: ICreatePolicyApplianceReq,
    collectedInternally: boolean,
  ) => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    setIsLoading(true);
    try {
      if (collectedInternally) {
        const res = await preCreatePolicyPaymentAppliance(data);
        if (res) {
          setInfoPayment({
            pregeneratePolicyId: res.data.pregeneratePolicyId,
          });
          setStep(3);
        }
      } else {
        const res = await createPolicyAppliance(data);
        if (res) {
          setShow((prevState) => ({
            ...prevState,
            visible: true,
            header: <ModalHeader success={true} />,
            content: (
              <ModalBody
                title="Creación póliza exitosa"
                message={`La póliza se creó exitosamente con id: ${res.data.policy.id}`}
              />
            ),
            onAccept: clearProcess,
            titleOnAccept: "Aceptar",
            withClass: "flex",
            showBtnAccept: true,
          }));
        }
      }
    } catch (error) {
      showErrorModal(error);
    } finally {
      submittingRef.current = false;
      setIsLoading(false);
    }
  };

  const onSubmit = async (val: IInputs) => {
    const selectedOption = planData?.pricingOptions?.find(
      (opt: any) => opt.id === Number(val.plan),
    );
    const collectedInternally = !!selectedOption?.collectedInternally;

    if (!goesThroughTouch && !collectedInternally && !val.confirm) {
      setShow((prevState) => ({
        ...prevState,
        visible: true,
        header: <ModalHeader success={false} />,
        content: (
          <ModalBody
            title="Confirmación requerida"
            message="Debes confirmar que ya facturaste el producto al cliente para continuar."
          />
        ),
        onAccept: () =>
          setShow((prevState) => ({ ...prevState, visible: false })),
        titleOnAccept: "Aceptar",
        withClass: "flex",
        showBtnAccept: true,
      }));
      return;
    }

    setFormData({ ...val, plan: planData, priceOptionId: Number(val.plan) });

    if (isAppliance) {
      const data = buildAppliancePolicyData(val);
      await finalizeAppliance(data, collectedInternally);
      return;
    }

    const data = buildPhonePolicyData(val);

    // Dispositivo usado -> primero la prueba de touch, luego se finaliza con el
    // touchId inyectado en el payload de creación.
    if (goesThroughTouch) {
      setPendingData(data);
      setPendingCollectedInternally(collectedInternally);
      setStep(2);
      return;
    }

    await finalize(data, collectedInternally);
  };

  const handleTouchValidated = async (touchId: string) => {
    if (!pendingData) return;
    await finalize({ ...pendingData, touchId }, pendingCollectedInternally);
  };

  const clearProcess = async () => {
    await downloadScreenShoot();
    reset();
    setStep(1);
    setSelectedPlan(null);
    setPlanData(null);
    setPendingData(null);
    setPendingCollectedInternally(false);
    setInfoPayment(null);
    if (isMaccenter) {
      setDeviceKind(null);
    }
    setShow((prevState) => ({
      ...prevState,
      visible: false,
    }));
  };

  const handleBackToDeviceKind = () => {
    reset();
    setSelectedPlan(null);
    setPlanData(null);
    setDeviceKind(null);
  };

  const downloadScreenShoot = async () => {
    const newDate = new Date();
    const formattedDate = newDate
      .toISOString()
      .split("T")[0]
      .replaceAll("-", "");
    if (!modalRef.current) return;

    const canvas = await html2canvas(modalRef.current);
    const dataUrl = canvas.toDataURL("image/png");

    const link = document.createElement("a");
    link.href = dataUrl;
    link.download = `${getValues(isAppliance ? "serial" : "imei")}${config.sponsorID}${formattedDate}.png`;
    link.click();
  };

  // El check "ya facturé" solo se muestra cuando el plan se cobra offline
  // (collectedInternally === false) y no hay touch de por medio.
  const liveOption = planData?.pricingOptions?.find(
    (opt: any) => opt.id === Number(watch("plan")),
  );
  const showConfirm =
    !goesThroughTouch && !!liveOption && !liveOption.collectedInternally;

  // MacCenter, sin dispositivo elegido todavía: solo se muestra el selector.
  if (isMaccenter && step === 1 && !deviceKind) {
    return <DeviceKindSelectComponent onSelect={setDeviceKind} />;
  }

  return (
    <>
      {step === 1 && (
        <form onSubmit={handleSubmit(onSubmit)}>
          <UserDataComponent control={control} errors={errors} />
          {isAppliance ? (
            <ApplianceDeviceComponent
              control={control}
              watch={watch}
              clearErrors={clearErrors}
              setValue={setValue}
              errors={errors}
              setSelectedPlan={setSelectedPlan}
              setPlanData={setPlanData}
            />
          ) : (
            <DeviceComponent
              control={control}
              watch={watch}
              clearErrors={clearErrors}
              setValue={setValue}
              errors={errors}
              setSelectedPlan={setSelectedPlan}
              setPlanData={setPlanData}
              setBackendInsuredValue={setBackendInsuredValue}
              flow={flow}
            />
          )}
          {showConfirm && (
            <div className="col-span-2 w-full rounded-xl bg-gray p-3">
              <LabelCheckbox>
                <input
                  {...register("confirm")}
                  id="confirm"
                  name="confirm"
                  type="checkbox"
                  className="mt-5"
                />
                <i></i> Confirmo que ya facturé el producto al cliente
                <span className="text-3xl text-critical">*</span>
              </LabelCheckbox>
              <div className="mb-5 h-1">
                {errors && (
                  <p className="my-0 py-0 text-base text-critical">
                    {errors.confirm?.message}
                  </p>
                )}
              </div>
            </div>
          )}
          <div
            className={`flex w-full ${
              (config.flows?.length || 0) > 1 || isMaccenter
                ? "justify-between"
                : "justify-end"
            }`}
          >
            {isMaccenter ? (
              <PrimaryButton
                title="Atrás"
                type="button"
                className={"mt-5 rounded-3xl border px-5 py-1 text-textBlack"}
                style={{ borderColor: "#0a0a0a" }}
                onClick={handleBackToDeviceKind}
              />
            ) : (
              (config.flows?.length || 0) > 1 && (
                <PrimaryButton
                  title="Atrás"
                  type="button"
                  className={"mt-5 rounded-3xl border px-5 py-1 text-textBlack"}
                  style={{ borderColor: "#0a0a0a" }}
                  onClick={() => setGeneralStep(3)}
                />
              )
            )}
            <PrimaryButton
              title={goesThroughTouch ? "Siguiente" : "Asegurar"}
              className={
                "text-md mt-5 w-36 rounded-3xl bg-textBlack px-5 py-1 font-normal text-whiteSoft"
              }
              type="submit"
              disabled={isLoading || isSubmitting}
              loading={isLoading || isSubmitting}
            />
          </div>
        </form>
      )}
      {step === 2 && (
        <TouchComponent
          imei={pendingData?.device?.imei || ""}
          loading={isLoading}
          nextLabel={pendingCollectedInternally ? "Siguiente" : "Asegurar"}
          onValidated={handleTouchValidated}
          onBack={() => setStep(1)}
        />
      )}
      {step === 3 && <PaymentComponent />}
      {step === 4 && <StatusPaymentComponent />}
    </>
  );
};

export default CreateSimplyComponent;
